import { ComponentRef, forwardRef } from "react";
import { Button, ButtonProps } from "@mui/material";
import { Check, Publish, Save, Send, Sync } from "@mui/icons-material";
import { SubmittableState, ValidateableState } from "@/app/lib/hooks/use-editor/types/types";

type SubmitSemantic = "send" | "publish" | "save" | "apply";

interface SubmitButtonProps {
    semantic?: SubmitSemantic;
    state?: SubmittableState & Pick<ValidateableState, "isValid" | "error">;
}

const LABELS: Record<SubmitSemantic, { idle: string; pending: string }> = {
    save: { idle: "Speichern", pending: "Speichert …" },
    publish: { idle: "Veröffentlichen", pending: "Veröffentlicht …" },
    send: { idle: "Senden", pending: "Sendet …" },
    apply: { idle: "Übernehmen", pending: "Übernimmt …" },
};

const ICONS = {
    save: Save,
    publish: Publish,
    send: Send,
    apply: Check,
} as const;

const SubmitButton = forwardRef<
    ComponentRef<typeof Button>,
    ButtonProps & SubmitButtonProps
>(function SubmitButton({ children, semantic = "save", state, disabled, color, ...props }, ref) {
    const isSubmitting = Boolean(state?.isSubmitting);
    const hasFailed = state?.success === false && Boolean(state.error);
    const hasSucceeded = state?.success === true;
    const SemanticIcon = ICONS[semantic];

    const resolvedColor: ButtonProps["color"] = hasFailed
        ? "error"
        : hasSucceeded
            ? "success"
            : color ?? "primary";
    const resolvedLabel = isSubmitting
        ? LABELS[semantic].pending
        : hasFailed
            ? "Fehler"
            : hasSucceeded
                ? (semantic === "publish" ? "Veröffentlicht" : "Erfolgreich")
                : LABELS[semantic].idle;

    return (
        <Button
            {...props}
            ref={ref}
            color={resolvedColor}
            disabled={disabled || isSubmitting || state?.isValid === false}
            startIcon={isSubmitting
                ? <Sync className="origin-center animate-spin" />
                : <SemanticIcon />}
            aria-busy={isSubmitting || undefined}
        >
            {children ?? resolvedLabel}
        </Button>
    );
});

export default SubmitButton;
